



import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { encryptPayload, decryptResponse } from "./../CryptoUtil";
import { api } from "./../../App";
import indiaData from "./../../data/location_data.json";

const SEP = "|||";
const DEFAULT_ZSM = "ZSM1000";
const DEFAULT_ASM = "ASM1000";
const DEFAULT_SM = "SM1000";

const EMPTY_FORM = {
  zsm_id: "",
  asm_id: "",
  sm_id: "",
  name: "",
  business_name: "",
  email: "",
  phone: "",
  password: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
};

const normalize = (value) =>
  String(value ?? "").trim().replace(/\s+/g, " ").toUpperCase();

const getSession = () => {
  try {
    return JSON.parse(localStorage.getItem("userSession") || "null");
  } catch {
    return null;
  }
};

const getAuthHeaders = () => {
  const session = getSession();

  return {
    Accept: "application/json",
    ...(session?.token
      ? { Authorization: `Bearer ${session.token}` }
      : {}),
  };
};

const parseJson = (value) => {
  if (typeof value !== "string") return value;

  const text = value.trim();
  if (!text) return [];

  try {
    return JSON.parse(text);
  } catch {
    return text.split(",").map((item) => item.trim()).filter(Boolean);
  }
};

const decodeApiResponse = (raw) => {
  if (raw?.encrypted?.payload !== undefined) {
    return decryptResponse(
      raw.encrypted.payload,
      raw.encrypted.transactionId
    );
  }

  if (raw?.payload !== undefined && raw?.transactionId) {
    try {
      return decryptResponse(raw.payload, raw.transactionId);
    } catch {
      return raw;
    }
  }

  return raw;
};

const getRecords = (raw, keys = []) => {
  const result = decodeApiResponse(raw);
  const candidates = [result, result?.data, result?.result, result?.records];
  for (const item of candidates) {
    if (Array.isArray(item)) return item;
    if (item && typeof item === "object") {
      for (const key of [...keys, "data", "records", "list", "items"]) {
        if (Array.isArray(item[key])) return item[key];
      }
    }
  }
  return [];
};

const getId = (record, kind = "") => {
  if (!record) return "";
  const fields = {
    zsm: ["zsm_id", "zsm_code", "id", "code", "user_code"],
    asm: ["asm_id", "asm_code", "id", "code", "user_code"],
    sm: ["sm_id", "sm_code", "id", "code", "user_code"],
  };
  return String((fields[kind] || ["id", "code", "user_code", "zsm_id", "asm_id", "sm_id"])
    .map((key) => record[key]).find((value) => value != null && value !== "") ?? "").trim();
};

const getName = (record) =>
  String(record?.name ?? record?.full_name ?? record?.username ?? "").trim();

const isActive = (record) =>
  !["INACTIVE", "DISABLED", "DELETED", "0", "FALSE"].includes(
    normalize(record?.status ?? "ACTIVE")
  );

const parseNamedEntries = (value) => {
  value = parseJson(value);

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string" || typeof item === "number") {
          return [String(item).trim(), null];
        }

        const name =
          item?.name ??
          item?.state ??
          item?.district ??
          item?.city ??
          item?.tehsil ??
          item?.title;

        return name ? [String(name).trim(), item] : null;
      })
      .filter(Boolean);
  }

  if (value && typeof value === "object") {
    return Object.entries(value);
  }

  return [];
};

const getDistrictEntries = (stateValue) => {
  if (Array.isArray(stateValue)) {
    return stateValue
      .map((item) => {
        if (typeof item === "string") return [item, null];

        const name = item?.district ?? item?.name ?? item?.title;
        return name ? [String(name), item] : null;
      })
      .filter(Boolean);
  }

  if (!stateValue || typeof stateValue !== "object") return [];

  const nested =
    stateValue.districts ??
    stateValue.district ??
    stateValue.data ??
    stateValue.children;

  return nested && typeof nested === "object"
    ? parseNamedEntries(nested)
    : Object.entries(stateValue);
};

const getCityNames = (districtValue) => {
  if (Array.isArray(districtValue)) {
    return [
      ...new Set(
        districtValue
          .map((item) =>
            typeof item === "string" || typeof item === "number"
              ? String(item).trim()
              : String(
                  item?.city ??
                    item?.name ??
                    item?.town ??
                    item?.tehsil ??
                    ""
                ).trim()
          )
          .filter(Boolean)
      ),
    ];
  }

  if (!districtValue || typeof districtValue !== "object") return [];

  const nested =
    districtValue.cities ??
    districtValue.city ??
    districtValue.towns ??
    districtValue.tehsils ??
    districtValue.tehsil ??
    districtValue.children;

  if (Array.isArray(nested)) {
    return [
      ...new Set(
        nested
          .map((item) =>
            typeof item === "string"
              ? item.trim()
              : String(item?.name ?? item?.city ?? item?.tehsil ?? "").trim()
          )
          .filter(Boolean)
      ),
    ];
  }

  if (nested && typeof nested === "object") {
    return Object.keys(nested);
  }

  const metadata = new Set([
    "name",
    "district",
    "state",
    "id",
    "code",
    "status",
    "created_at",
    "updated_at",
  ]);

  return Object.keys(districtValue).filter(
    (key) => !metadata.has(key.toLowerCase())
  );
};

const buildLocationTree = (data) => {
  if (!data || typeof data !== "object") return [];

  const root = data.states ?? data.data ?? data;

  return parseNamedEntries(root)
    .map(([stateName, stateValue]) => ({
      name: String(stateName).trim(),
      districts: getDistrictEntries(stateValue)
        .map(([districtName, districtValue]) => ({
          name: String(districtName).trim(),
          cities: getCityNames(districtValue).sort((a, b) =>
            a.localeCompare(b)
          ),
        }))
        .filter((district) => district.name),
    }))
    .filter((state) => state.name)
    .sort((a, b) => a.name.localeCompare(b.name));
};

const getCoverage = (record) => {
  if (!record) return null;

  const raw =
    record.working_area_new ??
    record.working_area ??
    record.working_states ??
    record.working_state ??
    record.working_districts ??
    record.working_cities;

  if (raw == null || raw === "") return null;
  return parseJson(raw);
};

/*
 * Converts manager coverage into a canonical hierarchy:
 * {
 *   "Madhya Pradesh": {
 *     "Bhopal": ["Berasia", "Huzur"]
 *   }
 * }
 */
const buildAllowedHierarchy = (coverage, masterTree, allowAll = false) => {
  const master = Object.fromEntries(
    masterTree.map((state) => [
      state.name,
      Object.fromEntries(
        state.districts.map((district) => [district.name, district.cities])
      ),
    ])
  );

  if (allowAll) return master;
  if (coverage == null) return {};

  const data = parseJson(coverage);
  if (!data || typeof data !== "object") return {};

  const result = {};

  const addState = (stateName, districtData) => {
    const state = masterTree.find(
      (item) => normalize(item.name) === normalize(stateName)
    );

    if (!state) return;

    if (districtData == null || districtData === true || districtData === "all") {
      result[state.name] = { ...master[state.name] };
      return;
    }

    if (Array.isArray(districtData)) {
      const allStrings = districtData.every(
        (item) => typeof item === "string"
      );

      if (allStrings) {
        const districts = {};

        districtData.forEach((districtName) => {
          const district = state.districts.find(
            (item) => normalize(item.name) === normalize(districtName)
          );

          if (district) districts[district.name] = district.cities;
        });

        if (Object.keys(districts).length) result[state.name] = districts;
        return;
      }

      districtData.forEach((item) => {
        const districtName = item?.district ?? item?.name;
        if (!districtName) return;

        const district = state.districts.find(
          (entry) => normalize(entry.name) === normalize(districtName)
        );

        if (!district) return;

        const cityData =
          item?.cities ?? item?.city ?? item?.tehsils ?? item?.tehsil;

        if (!cityData) {
          result[state.name] ??= {};
          result[state.name][district.name] = district.cities;
          return;
        }

        const requestedCities = Array.isArray(cityData)
          ? cityData.map((city) =>
              typeof city === "string"
                ? city
                : city?.name ?? city?.city ?? city?.tehsil ?? ""
            )
          : Object.keys(cityData || {});

        const cities = district.cities.filter((city) =>
          requestedCities.some(
            (requested) => normalize(requested) === normalize(city)
          )
        );

        if (cities.length) {
          result[state.name] ??= {};
          result[state.name][district.name] = cities;
        }
      });

      return;
    }

    if (typeof districtData === "object") {
      const districts = {};

      Object.entries(districtData).forEach(([districtName, cityData]) => {
        const district = state.districts.find(
          (item) => normalize(item.name) === normalize(districtName)
        );

        if (!district) return;

        if (
          cityData == null ||
          cityData === true ||
          cityData === "all" ||
          (typeof cityData === "object" && !Object.keys(cityData).length)
        ) {
          districts[district.name] = district.cities;
          return;
        }

        const rawCities = Array.isArray(cityData)
          ? cityData.map((city) =>
              typeof city === "string"
                ? city
                : city?.name ?? city?.city ?? city?.tehsil ?? ""
            )
          : typeof cityData === "object"
          ? Object.keys(cityData)
          : [];

        const cities = district.cities.filter((city) =>
          rawCities.some((requested) => normalize(requested) === normalize(city))
        );

        if (cities.length) districts[district.name] = cities;
      });

      if (Object.keys(districts).length) result[state.name] = districts;
    }
  };

  if (Array.isArray(data)) {
    data.forEach((item) => {
      if (typeof item === "string") {
        addState(item, null);
      } else if (item && typeof item === "object") {
        const stateName = item.state ?? item.state_name ?? item.name;
        if (stateName) {
          addState(stateName, item.districts ?? item.working_area ?? null);
        }
      }
    });

    return result;
  }

  Object.entries(data).forEach(([stateName, districtData]) => {
    addState(stateName, districtData);
  });

  return result;
};

const getApiError = (err, fallback) => {
  try { return decodeApiResponse(err.response?.data)?.message || err.message || fallback; }
  catch { return err.message || fallback; }
};

const Dealer = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [zsms, setZsms] = useState([]);
  const [asms, setAsms] = useState([]);
  const [sms, setSms] = useState([]);
  const [selectedArea, setSelectedArea] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedStates, setExpandedStates] = useState({});
  const [expandedDistricts, setExpandedDistricts] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loadingZsms, setLoadingZsms] = useState(true);
  const [loadingAsms, setLoadingAsms] = useState(false);
  const [loadingSm, setLoadingSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const districtRefs = useRef({});
  const workingAreaRef = useRef(null);

  const session = getSession();
  const role = normalize(session?.user?.role);
  const userCode = String(
    session?.user?.code ?? session?.user?.user_code ?? ""
  ).trim();
    const allLocations = useMemo(() => buildLocationTree(indiaData), []);

  const selectedZsm = useMemo(
    () =>
      zsms.find(
        (item) => normalize(getId(item)) === normalize(formData.zsm_id)
      ) || null,
    [zsms, formData.zsm_id]
  );

  const selectedAsm = useMemo(
    () =>
      asms.find(
        (item) => normalize(getId(item)) === normalize(formData.asm_id)
      ) || null,
    [asms, formData.asm_id]
  );

    const selectedSm = useMemo(
    () =>
      sms.find(
        (item) => normalize(getId(item)) === normalize(formData.sm_id)
      ) || null,
    [sms, formData.sm_id]
  );

  const isDefaultSm = normalize(formData.sm_id) === DEFAULT_SM;
  const isDefaultZsm = normalize(formData.zsm_id) === DEFAULT_ZSM;
  const isDefaultAsm =
    !formData.asm_id || normalize(formData.asm_id) === DEFAULT_ASM;

  const allowedLocations = useMemo(() => {
    if (!selectedZsm || !selectedAsm || !selectedSm) return [];

    // Default ZSM + default ASM means all master locations.


    const zsmHierarchy = buildAllowedHierarchy(
      getCoverage(selectedZsm),
      allLocations,
      isDefaultZsm
    );

    let effectiveHierarchy = zsmHierarchy;

    if (selectedAsm && normalize(getId(selectedAsm)) !== DEFAULT_ASM) {
      const asmHierarchy = buildAllowedHierarchy(
        getCoverage(selectedAsm),
        allLocations,
        false
      );

      const intersection = {};

      Object.entries(asmHierarchy).forEach(([state, districts]) => {
        if (!zsmHierarchy[state]) return;

        Object.entries(districts).forEach(([district, cities]) => {
          const zsmCities = zsmHierarchy[state][district];
          if (!zsmCities) return;

          const commonCities = cities.filter((city) =>
            zsmCities.some((allowed) => normalize(allowed) === normalize(city))
          );

          if (commonCities.length) {
            intersection[state] ??= {};
            intersection[state][district] = commonCities;
          }
        });
      });

      effectiveHierarchy = intersection;
    }
    if (!selectedSm) return [];
    const smHierarchy = isDefaultSm ? effectiveHierarchy : buildAllowedHierarchy(
      getCoverage(selectedSm), allLocations, false
    );
    const finalHierarchy = {};
    Object.entries(effectiveHierarchy).forEach(([state, districts]) => {
      Object.entries(districts).forEach(([district, cities]) => {
        const smCities = smHierarchy[state]?.[district] || [];
        const common = cities.filter((city) => smCities.some((value) => normalize(value) === normalize(city)));
        if (common.length) {
          finalHierarchy[state] ??= {};
          finalHierarchy[state][district] = common;
        }
      });
    });
    effectiveHierarchy = finalHierarchy;


    return allLocations
      .map((state) => {
        const districts = effectiveHierarchy[state.name];

        if (!districts) return null;

        return {
          ...state,
          districts: state.districts
            .filter((district) => districts[district.name])
            .map((district) => ({
              ...district,
              cities: district.cities.filter((city) =>
                districts[district.name].some(
                  (allowed) => normalize(allowed) === normalize(city)
                )
              ),
            }))
            .filter((district) => district.cities.length > 0),
        };
      })
      .filter((state) => state && state.districts.length > 0);
  }, [
    allLocations,
    selectedZsm,
    selectedAsm,
    selectedSm,
    isDefaultSm,
    isDefaultZsm,
    isDefaultAsm,
  ]);

  const residentialDistricts = useMemo(() => {
    const state = allLocations.find(
      (item) => item.name === formData.state
    );

    return state?.districts.map((item) => item.name) || [];
  }, [allLocations, formData.state]);

  const filteredLocations = useMemo(() => {
    const query = normalize(searchTerm);

    // Keep the full location list hidden until the user searches.
    if (!query) return [];

    return allowedLocations
      .map((state) => {
        const stateMatches = normalize(state.name).includes(query);

        const districts = state.districts
          .map((district) => {
            const districtMatches = normalize(district.name).includes(query);
            const matchingCities = district.cities.filter((city) =>
              normalize(city).includes(query)
            );

            if (stateMatches || districtMatches) return district;

            return matchingCities.length
              ? { ...district, cities: matchingCities }
              : null;
          })
          .filter(Boolean);

        return stateMatches || districts.length
          ? { ...state, districts }
          : null;
      })
      .filter(Boolean);
  }, [allowedLocations, searchTerm]);

  const selectedCount = useMemo(
    () =>
      Object.values(selectedArea).reduce(
        (total, districts) =>
          total +
          Object.values(districts || {}).reduce(
            (sum, cities) => sum + (Array.isArray(cities) ? cities.length : 0),
            0
          ),
        0
      ),
    [selectedArea]
  );

  const selectedTags = useMemo(
    () =>
      Object.entries(selectedArea).flatMap(([state, districts]) =>
        Object.entries(districts).flatMap(([district, cities]) =>
          cities.map((city) => ({
            state,
            district,
            city,
            key: `${state}${SEP}${district}${SEP}${city}`,
          }))
        )
      ),
    [selectedArea]
  );

  useEffect(() => {
    let active = true;

    const loadZsms = async () => {
      setLoadingZsms(true);

      try {
        const currentSession = getSession();
        const currentRole = normalize(currentSession?.user?.role);
        const currentCode = String(
          currentSession?.user?.code ??
            currentSession?.user?.user_code ??
            ""
        ).trim();

        const response = await axios.get(`${api}getZsm.php`, {
          withCredentials: true,
          headers: getAuthHeaders(),
          timeout: 15000,
        });

        const records = getRecords(response.data, ["zsms", "zsm"]);
        let activeRecords = records.filter(isActive);

        // ADMIN can always choose the system Default ZSM, even when getZsm.php
        // returns only physical database rows or the default row is not stored.
        // A logged-in ZSM must never be allowed to switch to another ZSM.
        if (
          currentRole === "ADMIN" &&
          !activeRecords.some(
            (item) => normalize(getId(item)) === DEFAULT_ZSM
          )
        ) {
          activeRecords = [
            {
              id: DEFAULT_ZSM,
              name: "Default ZSM (All permitted locations)",
              status: "active",
              working_area: null,
              working_area_new: null,
              is_default: true,
            },
            ...activeRecords,
          ];
        }

        const filtered =
          currentRole === "ZSM"
            ? activeRecords.filter(
                (item) => normalize(getId(item)) === normalize(currentCode)
              )
            : activeRecords;

        if (!active) return;

        setZsms(filtered);

        if (currentRole === "ZSM" && filtered.length) {
          setFormData((prev) => ({
            ...prev,
            zsm_id: getId(filtered[0]),
            asm_id: "",
            sm_id: "",
          }));
        } else if (
          filtered.some((item) => normalize(getId(item)) === DEFAULT_ZSM)
        ) {
          setFormData((prev) => ({
            ...prev,
            zsm_id: DEFAULT_ZSM,
            asm_id: "",
            sm_id: "",
          }));
        } else if (filtered.length === 1) {
          setFormData((prev) => ({
            ...prev,
            zsm_id: getId(filtered[0]),
            asm_id: "",
            sm_id: "",
          }));
        } else if (!filtered.length) {
          setError("No active ZSM records found.");
        }
      } catch (err) {
        console.error("GET ZSM ERROR:", err);

        if (active) {
          let message = getApiError(err, "Unable to load ZSM records.");

          if (err.response?.status === 403) {
            message =
              "Access denied by getZsm.php (403). Please check your " +
              "Admin/ZSM login, session permissions and Bearer token. " +
              "The server must authorize this request.";
          } else if (err.response?.status === 401) {
            message = "Session expired. Please log in again.";
          }

          setZsms([]);
          setError(message);
        }
      } finally {
        if (active) setLoadingZsms(false);
      }
    };

    loadZsms();

    return () => {
      active = false;
    };
  }, []);

  // getAsm.php is scoped to one ZSM. For the default ZSM, request each
  // permitted physical ZSM separately and merge the results on the client.
  useEffect(() => {
    let active = true;
    const zsmId = formData.zsm_id;
    setAsms([]);
    setSms([]);
    setFormData(prev => ({ ...prev, asm_id: "", sm_id: "" }));
    if (!zsmId || loadingZsms) {
      setLoadingAsms(false);
      return () => { active = false; };
    }
    const load = async () => {
      setLoadingAsms(true);
      try {
        const allZsms = normalize(zsmId) === DEFAULT_ZSM;
        const ids = allZsms
          ? [...new Set(zsms.map(item => getId(item, "zsm")).filter(id => id && normalize(id) !== DEFAULT_ZSM))]
          : [zsmId];
        // Query default ZSM too, because it may have its own actual ASMs.
        if (allZsms) ids.unshift(DEFAULT_ZSM);
        const responses = await Promise.allSettled(ids.map(id => axios.get(`${api}getAsm.php`, {
          params: { zsm_id: id }, withCredentials: true,
          headers: getAuthHeaders(), timeout: 15000
        })));
        const failed = responses.filter(r => r.status === "rejected");
        const rows = [];
        responses.forEach((result, index) => {
          if (result.status !== "fulfilled") return;
          const decoded = decodeApiResponse(result.value.data);
          if (decoded?.success === false) return;
          getRecords(result.value.data, ["asms", "asm"]).forEach(item => {
            if (!isActive(item) || !getId(item, "asm")) return;
            const parent = item.zsm_id ?? item.reporting_zsm_id ?? item.parent_zsm_id;
            if (parent && normalize(parent) !== normalize(ids[index])) return;
            rows.push(item);
          });
        });
        if (responses.length && failed.length === responses.length) {
          throw failed[0].reason;
        }
        const unique = new Map();
        rows.forEach(item => unique.set(normalize(getId(item, "asm")), item));
        // Default ASM is always visible, even if real ASMs exist.
        const defaultAsm = unique.get(DEFAULT_ASM) || {
          id: DEFAULT_ASM, zsm_id: zsmId, name: "Default ASM",
          status: "active", working_area: null, is_default: true
        };
        unique.delete(DEFAULT_ASM);
        const finalRows = [defaultAsm, ...[...unique.values()].sort((x, y) => getName(x).localeCompare(getName(y)))];
        if (!active) return;
        setAsms(finalRows);
        setFormData(prev => ({ ...prev, asm_id: DEFAULT_ASM, sm_id: "" }));
        if (failed.length) setError(`Some ASM records could not load (${failed.length} request(s) failed).`);
      } catch (err) {
        if (active) { setAsms([]); setError(getApiError(err, "Unable to load ASMs")); }
      } finally { if (active) setLoadingAsms(false); }
    };
    load();
    return () => { active = false; };
  }, [formData.zsm_id, loadingZsms, zsms]);

  // Fetch all permitted SMs without restrictive synthetic default IDs.
  // Client-side hierarchy filtering implements both default and specific modes.
  useEffect(() => {
    let active = true;
    const zsmId = formData.zsm_id;
    const asmId = formData.asm_id;
    setSms([]);
    setFormData(prev => ({ ...prev, sm_id: "" }));
    if (!zsmId || !asmId || loadingAsms) {
      setLoadingSm(false);
      return () => { active = false; };
    }
    const load = async () => {
      setLoadingSm(true);
      try {
        // For ADMIN, getSm.php without filters returns all admin-owned SMs.
        // For ZSM, it returns all SMs under the logged-in ZSM.
        const response = await axios.get(`${api}getSm.php`, {
          withCredentials: true, headers: getAuthHeaders(), timeout: 15000
        });
        const decoded = decodeApiResponse(response.data);
        if (decoded?.success === false) throw new Error(decoded.message || "SM API error");
        const isAllZsm = normalize(zsmId) === DEFAULT_ZSM;
        const isAllAsm = normalize(asmId) === DEFAULT_ASM;
        const rows = getRecords(response.data, ["sms", "sm"])
          .filter(item => isActive(item) && Boolean(getId(item, "sm")))
          .filter(item => {
            const parentZsm = item.zsm_id ?? item.reporting_zsm_id ?? item.parent_zsm_id;
            const parentAsm = item.asm_id ?? item.reporting_asm_id ?? item.parent_asm_id;
            if (!isAllZsm && normalize(parentZsm) !== normalize(zsmId)) return false;
            if (!isAllAsm && normalize(parentAsm) !== normalize(asmId)) return false;
            return true;
          });
        const unique = new Map();
        rows.forEach(item => unique.set(normalize(getId(item, "sm")), item));
        // Default SM is always visible, including when real SMs exist.
        const defaultSm = unique.get(DEFAULT_SM) || {
          id: DEFAULT_SM, zsm_id: zsmId, asm_id: asmId,
          name: "Default SM", status: "active", working_area: null, is_default: true
        };
        unique.delete(DEFAULT_SM);
        const finalRows = [defaultSm, ...[...unique.values()].sort((x, y) => getName(x).localeCompare(getName(y)))];
        if (!active) return;
        setSms(finalRows);
        setFormData(prev => ({ ...prev, sm_id: DEFAULT_SM }));
      } catch (err) {
        if (active) { setSms([]); setError(getApiError(err, "Unable to load SMs")); }
      } finally { if (active) setLoadingSm(false); }
    };
    load();
    return () => { active = false; };
  }, [formData.zsm_id, formData.asm_id, loadingAsms]);

  useEffect(() => {
    allowedLocations.forEach((state) => {
      state.districts.forEach((district) => {
        const key = `${state.name}${SEP}${district.name}`;
        const element = districtRefs.current[key];

        if (!element) return;

        const selected = selectedArea?.[state.name]?.[district.name] || [];
        element.indeterminate =
          selected.length > 0 && selected.length < district.cities.length;
      });
    });
  }, [allowedLocations, selectedArea]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

    const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "state" ? { city: "" } : {}),
    }));

    clearMessages();
  };

  const handleZsmChange = (event) => {
    const zsmId = event.target.value;

    setFormData((prev) => ({
      ...prev,
      zsm_id: zsmId,
      asm_id: "",
            sm_id: "",
    }));

    setSelectedArea({});
    setSearchTerm("");
    setExpandedStates({});
    setExpandedDistricts({});
    clearMessages();
  };

  const handleAsmChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      asm_id: event.target.value,
      sm_id: "",
    }));

    setSelectedArea({});
    setSearchTerm("");
    setExpandedStates({});
    setExpandedDistricts({});
    clearMessages();
  };

  const handleSmChange = (event) => {
    setFormData((prev) => ({ ...prev, sm_id: event.target.value }));
    setSelectedArea({});
    clearMessages();
  };

  const toggleState = (stateName) => {
    setExpandedStates((prev) => ({
      ...prev,
      [stateName]: !prev[stateName],
    }));
  };

  const toggleDistrictExpanded = (stateName, districtName) => {
    const key = `${stateName}${SEP}${districtName}`;

    setExpandedDistricts((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isCitySelected = (state, district, city) =>
    Boolean(selectedArea?.[state]?.[district]?.includes(city));

  // A dealer can work in exactly one city/tehsil.
  const toggleCity = (stateName, districtName, cityName) => {
    const state = allowedLocations.find((item) => item.name === stateName);
    const district = state?.districts.find((item) => item.name === districtName);

    if (!district?.cities.includes(cityName)) {
      setError("This city is outside the selected manager's working area.");
      return;
    }

    if (isCitySelected(stateName, districtName, cityName)) {
      setSelectedArea({});
    } else {
      setSelectedArea({ [stateName]: { [districtName]: [cityName] } });
    }

    clearMessages();
  };

  // District checkbox selects its first city only, never the whole district.
  const toggleDistrict = (stateName, districtName) => {
    const state = allowedLocations.find((item) => item.name === stateName);
    const district = state?.districts.find((item) => item.name === districtName);
    const firstCity = district?.cities[0];

    if (!firstCity) {
      setError(`No cities/tehsils found for ${districtName}.`);
      return;
    }

    const alreadySelected = isCitySelected(stateName, districtName, firstCity);
    setSelectedArea(
      alreadySelected ? {} : { [stateName]: { [districtName]: [firstCity] } }
    );
    clearMessages();
  };

  const isDistrictChecked = (stateName, districtName) => {
    const state = allowedLocations.find((item) => item.name === stateName);
    const district = state?.districts.find(
      (item) => item.name === districtName
    );

    const cities = district?.cities || [];
    const selected = selectedArea?.[stateName]?.[districtName] || [];

    return (
      cities.length > 0 && cities.every((city) => selected.includes(city))
    );
  };

  const resetForm = () => {
    setFormData({
      ...EMPTY_FORM,
      zsm_id: role === "ZSM" ? userCode : DEFAULT_ZSM,
      asm_id: "",
            sm_id: "",
    });

    setSelectedArea({});
    setSearchTerm("");
    setExpandedStates({});
    setExpandedDistricts({});
    setShowPassword(false);
    clearMessages();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    clearMessages();

    const currentSession = getSession();
    const currentRole = normalize(currentSession?.user?.role);
    const currentCode = String(
      currentSession?.user?.code ??
        currentSession?.user?.user_code ??
        ""
    ).trim();

    if (
      !currentSession?.token ||
      !["ADMIN", "ZSM"].includes(currentRole) ||
      !currentCode
    ) {
      setError("Login session invalid. Please login again.");
      return;
    }

    if (!selectedZsm) {
      setError("Please select a reporting ZSM.");
      return;
    }

    if (
      currentRole === "ZSM" &&
      normalize(getId(selectedZsm)) !== normalize(currentCode)
    ) {
      setError("You can add a Dealer only under your own ZSM account.");
      return;
    }

    if (!selectedAsm || !selectedSm) {
      setError("Please select a valid ASM and SM before adding a Dealer.");
      return;
    }

    const name = String(formData.name ?? "").trim();
    const email = String(formData.email ?? "").trim().toLowerCase();
    const phone = String(formData.phone ?? "").trim();
    const businessName = String(formData.business_name ?? "").trim();

    if (!name || name.length > 100) {
      setError("Enter a valid Dealer name.");
      return;
    }

    if (businessName && businessName.length > 150) {
      setError("Business Name must be 150 characters or less.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address.");
      return;
    }

    if (!/^\d{10,15}$/.test(phone)) {
      setError("Phone must contain 10 to 15 digits.");
      return;
    }

    if (String(formData.password ?? "").length < 6) {
      setError("Password must contain at least 6 characters.");
      return;
    }

    if (formData.pincode && !/^\d{6}$/.test(String(formData.pincode ?? "").trim())) {
      setError("PIN code must contain exactly 6 digits.");
      return;
    }

    if (formData.state && !allLocations.some((item) => item.name === formData.state)) {
      setError("Select a valid residential state.");
      return;
    }

    if (
      formData.city &&
      !residentialDistricts.includes(formData.city)
    ) {
      setError("Select a valid residential district.");
      return;
    }

    // if (selectedCount !== 1) {
    //   setError("Please select exactly one working city/tehsil.");
    //   workingAreaRef.current?.scrollIntoView({
    //     behavior: "smooth",
    //     block: "center",
    //   });
    //   return;
    // }

    // Validate selected locations against the allowed hierarchy again.
    // for (const [stateName, districts] of Object.entries(selectedArea)) {
    //   const allowedState = allowedLocations.find(
    //     (item) => item.name === stateName
    //   );

    //   if (!allowedState) {
    //     setError(`State ${stateName} is outside the selected manager's coverage.`);
    //     return;
    //   }

    //   for (const [districtName, cities] of Object.entries(districts)) {
    //     const allowedDistrict = allowedState.districts.find(
    //       (item) => item.name === districtName
    //     );

    //     if (
    //       !allowedDistrict ||
    //       !Array.isArray(cities) ||
    //       !cities.length ||
    //       cities.some((city) => !allowedDistrict.cities.includes(city))
    //     ) {
    //       setError(`Invalid working-area selection: ${districtName}, ${stateName}.`);
    //       return;
    //     }
    //   }
    // }

    const payload = {
      zsm_id: getId(selectedZsm),
      asm_id: selectedAsm ? getId(selectedAsm) : DEFAULT_ASM,
      sm_id: getId(selectedSm, "sm"),
      name,
      business_name: businessName,
      email,
      phone,
      password: formData.password,
      address: String(formData.address ?? "").trim(),
      city: formData.city,
      state: formData.state,
      pincode: String(formData.pincode ?? "").trim(),
      // working_area: selectedArea,
    };

    setLoading(true);

    try {
      const response = await axios.post(
        `${api}addDealer.php`,
        encryptPayload(payload),
        {
          withCredentials: true,
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
          timeout: 20000,
        }
      );

      const result = decodeApiResponse(response.data);

      if (result?.success !== true && normalize(result?.status) !== "SUCCESS") {
        throw new Error(result?.message || "Unable to add Dealer.");
      }

      setSuccess(result.message || "Dealer added successfully.");

      setFormData({
        ...EMPTY_FORM,
        zsm_id: currentRole === "ZSM" ? currentCode : getId(selectedZsm),
        asm_id: getId(selectedAsm, "asm"),
        sm_id: getId(selectedSm, "sm"),
      });

      setSelectedArea({});
      setSearchTerm("");
      setExpandedStates({});
      setExpandedDistricts({});
      setShowPassword(false);
    } catch (err) {
      console.error("ADD DEALER ERROR:", {
        message: err.message,
        status: err.response?.status,
        response: err.response?.data,
        url: err.config?.url,
      });

      let message =
        err.response?.data?.message || err.message || "Unable to add Dealer.";

      if (err.response?.data) {
        try {
          const result = decodeApiResponse(err.response.data);
          message = result?.message || message;
        } catch {
          // Keep the best available error message.
        }
      }

      if (err.response?.status === 401) {
        message = "Login session missing or expired. Please login again.";
      } else if (err.response?.status === 403) {
        message = "You are not authorized to add a Dealer under this manager.";
      } else if (err.code === "ECONNABORTED") {
        message = "Request timed out. Check whether the Dealer was created before retrying.";
      } else if (err.code === "ERR_NETWORK") {
        message = "Backend connection failed. Check PHP server and CORS settings.";
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-12 grid-margin stretch-card">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title">Add Dealer</h4>
            <p className="card-description">
              Select a ZSM, select an ASM, select an SM, and assign permitted working locations.
            </p>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success" role="alert">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} autoComplete="off">
              <h5>Reporting Hierarchy</h5>

              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="zsm_id">Select ZSM *</label>
                  <select
                    id="zsm_id"
                    name="zsm_id"
                    className="form-control"
                    value={formData.zsm_id}
                    onChange={handleZsmChange}
                    disabled={loading || loadingZsms || role === "ZSM"}
                    required
                  >
                    <option value="">
                      {loadingZsms ? "Loading ZSMs..." : "Select ZSM"}
                    </option>

                    {zsms.map((zsm) => (
                      <option key={getId(zsm)} value={getId(zsm)}>
                        {getId(zsm)} - {getName(zsm) || (normalize(getId(zsm)) === DEFAULT_ZSM ? "Default ZSM" : "ZSM")}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6 form-group">
                  <label htmlFor="asm_id">Select ASM *</label>
                  <select
                    id="asm_id"
                    name="asm_id"
                    className="form-control"
                    value={formData.asm_id}
                    onChange={handleAsmChange}
                    disabled={!formData.zsm_id || loadingAsms || loading}
                    required
                  >
                    <option value="">
                      {loadingAsms ? "Loading ASMs..." : "Select ASM"}
                    </option>

                    {asms.map((asm) => (
                      <option key={getId(asm)} value={getId(asm)}>
                        {getId(asm)} - {getName(asm) || (normalize(getId(asm)) === DEFAULT_ASM ? "Default ASM" : "ASM")}
                      </option>
                    ))}
                  </select>
                </div>
                 <div className="col-md-6 form-group">
                  <label htmlFor="sm_id">Select SM *</label>
                  <select
                    id="sm_id"
                    name="sm_id"
                    className="form-control"
                    value={formData.sm_id}
                    onChange={handleSmChange}
                    disabled={!formData.asm_id || loadingSm || loading}
                    required
                  >
                    <option value="">
                      {loadingSm ? "Loading SMs..." : "Select SM"}
                    </option>

                    {sms.map((sm) => (
                      <option key={getId(sm, "sm")} value={getId(sm, "sm")}>
                        {getId(sm, "sm")} - {getName(sm) || "SM"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedZsm && (
                <div className="alert alert-info">
                  <strong>ZSM:</strong> {getId(selectedZsm)} - {getName(selectedZsm)}
                  <br />
                  <strong>ASM:</strong>{" "}
                  {selectedAsm
                    ? `${getId(selectedAsm)} - ${getName(selectedAsm)}`
                    : "Select ASM"}
                  <br />
                  <strong>Available states:</strong> {allowedLocations.length}
                </div>
              )}

              <hr />

              {/* <div ref={workingAreaRef}>
                <h5>Working Area</h5>
                <p className="card-description">
                  State → District → City / Tehsil. Select only one city/tehsil for each dealer.
                </p>

                                {!selectedZsm ? (
                  <div className="alert alert-warning">
                    Select ZSM first.
                  </div>
                ) : loadingAsms || loadingSm ? (
                  <div className="alert alert-info">Loading reporting hierarchy...</div>
                ) : !formData.asm_id ? (
                  <div className="alert alert-warning">Select ASM first.</div>
                ) : !formData.sm_id ? (
                  <div className="alert alert-warning">Select SM first.</div>
                ) : allowedLocations.length === 0 ? (
                  <div className="alert alert-warning">
                    No working locations found for this manager. Check the saved
                    working_area JSON.
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      {allowedLocations.map((state) => (
                        <span
                          key={state.name}
                          className="badge badge-info mr-2 mb-2 p-2"
                        >
                          {state.name}
                        </span>
                      ))}
                    </div>

                    <div className="form-group">
                      <label htmlFor="locationSearch">
                        Search State / District / City / Tehsil
                      </label>
                      <input
                        id="locationSearch"
                        type="search"
                        className="form-control"
                        placeholder="Search Madhya Pradesh, Bhopal or Huzur"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        disabled={loading}
                      />
                    </div>

                    <div className="mb-3">
                      <span className="badge badge-info mr-2">
                        Available states: {allowedLocations.length}
                      </span>
                      <span className="badge badge-info mr-2">
                        Selected cities/tehsils: {selectedCount} / 1
                      </span>
                    </div>

                    {!searchTerm.trim() ? (
                      <div className="alert alert-info">
                        Search by state, district, city, or tehsil to display
                        matching working locations.
                      </div>
                    ) : (
                      filteredLocations.map((state) => (
                        <div className="border rounded p-3 mb-3" key={state.name}>
                          <div className="d-flex justify-content-between align-items-center">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-left"
                              onClick={() => toggleState(state.name)}
                            >
                              <strong>
                                {expandedStates[state.name] ? "▼" : "▶"}{" "}
                                {state.name}
                              </strong>
                            </button>

                            <span className="badge badge-light">
                              {state.districts.length} districts
                            </span>
                          </div>

                          {(expandedStates[state.name] || searchTerm.trim()) &&
                            state.districts.map((district) => {
                              const districtKey = `${state.name}${SEP}${district.name}`;
                              const selected =
                                selectedArea?.[state.name]?.[district.name] || [];

                              const allDistrictCities =
                                allowedLocations
                                  .find((item) => item.name === state.name)
                                  ?.districts.find(
                                    (item) => item.name === district.name
                                  )?.cities || [];

                              return (
                                <div className="mt-3 ml-2" key={districtKey}>
                                  <div className="d-flex align-items-center flex-wrap">
                                    <input
                                      ref={(element) => {
                                        districtRefs.current[districtKey] = element;
                                      }}
                                      type="checkbox"
                                      className="mr-2"
                                      checked={isDistrictChecked(
                                        state.name,
                                        district.name
                                      )}
                                      onChange={() =>
                                        toggleDistrict(state.name, district.name)
                                      }
                                      disabled={loading}
                                    />

                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-left"
                                      onClick={() =>
                                        toggleDistrictExpanded(
                                          state.name,
                                          district.name
                                        )
                                      }
                                    >
                                      {expandedDistricts[districtKey] ||
                                      searchTerm.trim()
                                        ? "▼"
                                        : "▶"}{" "}
                                      <strong>{district.name}</strong>
                                    </button>

                                    <span className="badge badge-light ml-2">
                                      {selected.length} selected
                                    </span>
                                  </div>

                                  {(expandedDistricts[districtKey] ||
                                    searchTerm.trim()) && (
                                    <div className="row mt-2 ml-2">
                                      {district.cities.map((city) => (
                                        <div
                                          className="col-12 col-sm-6 col-lg-4 mb-2"
                                          key={`${districtKey}${SEP}${city}`}
                                        >
                                          <label className="d-flex align-items-center">
                                            <input
                                              type="checkbox"
                                              className="mr-2"
                                              checked={isCitySelected(
                                                state.name,
                                                district.name,
                                                city
                                              )}
                                              onChange={() =>
                                                toggleCity(
                                                  state.name,
                                                  district.name,
                                                  city
                                                )
                                              }
                                              disabled={loading}
                                            />
                                            <span>{city}</span>
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      ))
                    )}

                    {searchTerm.trim() && filteredLocations.length === 0 && (
                      <div className="alert alert-warning">
                        No matching state, district or city found in the
                        selected manager's allowed area.
                      </div>
                    )}
                  </>
                )}

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <strong>Selected Location ({selectedCount} / 1)</strong>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setSelectedArea({})}
                    disabled={loading || selectedCount === 0}
                  >
                    Clear All
                  </button>
                </div>

                <div className="mt-3">
                  {selectedTags.map((item) => (
                    <span
                      className="badge badge-primary p-2 mr-2 mb-2"
                      key={item.key}
                    >
                      {item.state} / {item.district} / {item.city}
                      <button
                        type="button"
                        className="btn btn-sm text-white ml-2 p-0"
                        aria-label={`Remove ${item.city}`}
                        onClick={() =>
                          toggleCity(item.state, item.district, item.city)
                        }
                        disabled={loading}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div> */}

              <hr />

              <h5>Dealer Account Details</h5>

              <div className="row">
                {[
                  ["name", "Full Name", "text"],
                   ["business_name", "Business Name", "text"],
                  ["email", "Email Address", "email"],
                  ["phone", "Mobile Number", "tel"],
                ].map(([name, label, type]) => (
                  <div className="col-md-6" key={name}>
                    <div className="form-group">
                      <label htmlFor={name}>{label} *</label>
                      <input
                        id={name}
                        name={name}
                        type={type}
                        className="form-control"
                        value={formData[name] ?? ""}
                        onChange={handleInputChange}
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>
                ))}

                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="password">Password *</label>
                    <div className="input-group">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        value={formData.password}
                        onChange={handleInputChange}
                        minLength={6}
                        disabled={loading}
                        required
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword((prev) => !prev)}
                          disabled={loading}
                        >
                          {showPassword ? "Hide" : "Show"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <hr />

              <h5>Residential Address</h5>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  className="form-control"
                  rows={3}
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>

              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="state">State</label>
                  <select
                    id="state"
                    name="state"
                    className="form-control"
                    value={formData.state}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="">Select State</option>
                    {allLocations.map((state) => (
                      <option key={state.name} value={state.name}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6 form-group">
                  <label htmlFor="city">District / City</label>
                  <select
                    id="city"
                    name="city"
                    className="form-control"
                    value={formData.city}
                    onChange={handleInputChange}
                    disabled={loading || !formData.state}
                  >
                    <option value="">Select District</option>
                    {residentialDistricts.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6 form-group">
                  <label htmlFor="pincode">PIN Code</label>
                  <input
                    id="pincode"
                    name="pincode"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    className="form-control"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary mr-2"
                disabled={loading || loadingZsms || loadingAsms || loadingSm || !selectedSm}
              >
                {loading ? "Saving Dealer..." : "Add Dealer"}
              </button>

              <button
                type="button"
                className="btn btn-light"
                onClick={resetForm}
                disabled={loading}
              >
                Reset Form
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dealer;